import type { Diff } from 'diff-match-patch';
import { DIFF_DELETE, DIFF_INSERT } from 'diff-match-patch';

import type { VisualDiffSegment } from './types';
import { RANGE_END, RANGE_START } from '../layered-diffs/constants';
import { diffWordsSemantic } from '../layered-diffs/word-diff';

function isRangeMarker(segment: VisualDiffSegment): boolean {
  return segment.text === RANGE_START || segment.text === RANGE_END;
}

/**
 * Whether a segment is a contiguous part of the change zone just before
 * RANGE_START.
 *
 * Deletions belong here for the same reason they always have — they describe
 * what's being replaced. `newInsert` segments are also walked through so we
 * can detect a "fusion" case: dmp sometimes emits a single delete + insert
 * chunk spanning two adjacent LLM changes when the boundary between them
 * lands in a region with repeated text its heuristics misalign. The newInsert
 * prefix that ends up immediately before RANGE_START would otherwise stop the
 * walk and hide the underlying newDelete. See {@link computePreRangeSegments}
 * for what we do once we've collected the segments.
 */
function isAdjacentChangeSegment(segment: VisualDiffSegment): boolean {
  if (isRangeMarker(segment)) return false;
  if (segment.newDelete) return true;
  if (segment.oldDelete) return true;
  if (segment.newInsert) return true;
  return false;
}

/**
 * Extracts only the portion of a visual diff that falls within range markers.
 *
 * Finds RANGE_START and RANGE_END markers in the visual diff and returns
 * only the segments between them. The markers themselves are removed from
 * the output.
 *
 * The range is expanded backwards to include any change segments that appear
 * immediately before RANGE_START. In the common case the adjacent segments
 * are deletions ("what was there before" the new content) and are emitted as-is.
 * In the "fusion" case dmp's main pass produced a single delete + insert chunk
 * spanning two distinct LLM changes; {@link computePreRangeSegments} re-diffs
 * the fused texts locally to recover the deletion that actually corresponds
 * to this range.
 *
 * If range markers are not found, returns an empty array.
 */
export function extractVisualDiffInRange(visualDiff: VisualDiffSegment[]): VisualDiffSegment[] {
  let startIndex = -1;
  let endIndex = -1;

  for (let i = 0; i < visualDiff.length; i++) {
    const segment = visualDiff[i];
    if (segment.text === RANGE_START && segment.newInsert) {
      startIndex = i;
    } else if (segment.text === RANGE_END && segment.newInsert) {
      endIndex = i;
      break;
    }
  }

  if (startIndex === -1 || endIndex === -1 || startIndex >= endIndex) {
    return [];
  }

  let expandedStartIndex = startIndex;
  while (expandedStartIndex > 0) {
    const prevSegment = visualDiff[expandedStartIndex - 1];
    if (isAdjacentChangeSegment(prevSegment)) {
      expandedStartIndex--;
    } else {
      break;
    }
  }

  const preRange = visualDiff.slice(expandedStartIndex, startIndex);
  const beforeSegments = computePreRangeSegments(preRange);

  const result: VisualDiffSegment[] = [];
  for (const segment of beforeSegments) {
    result.push({ ...segment });
  }
  for (let i = startIndex + 1; i < endIndex; i++) {
    result.push({ ...visualDiff[i] });
  }

  return result;
}

/**
 * Decides which of the change segments collected immediately before RANGE_START
 * should appear in the extracted diff.
 *
 * - No newInsert in the zone: pure deletion case, emit as-is. This is the
 *   original behaviour and what every non-fusion test exercises.
 * - newInsert present but no newDelete: the inserts belong to a different
 *   change that happens to sit right before this range. Discard them, but
 *   keep any independent oldDelete segments.
 * - Both newDelete and newInsert present: dmp produced a fused chunk. Re-diff
 *   the combined delete text against the combined insert text — dmp's
 *   heuristics behave well on the smaller, more localized input — and emit
 *   only the trailing run of deletes from the re-diff. The leading
 *   inserts/equals belong to the other change and are discarded.
 *
 * `oldDelete`-only segments (no `newDelete`, no `newInsert`) are preserved
 * through every branch because they aren't involved in the fusion.
 */
function computePreRangeSegments(segments: VisualDiffSegment[]): VisualDiffSegment[] {
  if (segments.length === 0) return [];

  const hasNewInsertPrefix = segments.some(s => s.newInsert);
  if (!hasNewInsertPrefix) {
    return segments.map(s => ({ ...s }));
  }

  const preservedOldDeletes = segments
    .filter(s => s.oldDelete && !s.newDelete && !s.newInsert)
    .map(s => ({ ...s }));

  let oldText = '';
  let newText = '';
  for (const seg of segments) {
    if (seg.newDelete) oldText += seg.text;
    if (seg.newInsert) newText += seg.text;
  }

  if (oldText.length === 0) {
    return preservedOldDeletes;
  }

  const realigned = diffWordsSemantic(oldText, newText);
  return [...preservedOldDeletes, ...extractTrailingDelete(realigned)];
}

/**
 * Returns the trailing run of delete operations from a re-diff as
 * VisualDiffSegments.
 *
 * Trailing inserts are skipped before collecting — an insert at the tail of
 * the re-diff sits between the deletion and the original RANGE_START position
 * in new-text order, which means it's content from a different change that
 * happens to come after the deletion. It does not belong in the range's
 * "what was replaced" view.
 */
function extractTrailingDelete(realigned: Diff[]): VisualDiffSegment[] {
  let i = realigned.length - 1;
  while (i >= 0 && realigned[i][0] === DIFF_INSERT) i--;

  const result: VisualDiffSegment[] = [];
  while (i >= 0 && realigned[i][0] === DIFF_DELETE) {
    result.unshift({ newDelete: true, text: realigned[i][1] });
    i--;
  }
  return result;
}
